import { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingCart, LayoutTemplate, Image, Tag, Star, Users, Settings, FileText, Layers, BookOpen, MessageSquare, Megaphone, Mail, Filter, Workflow, BarChart3 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import useSeo from "@/lib/useSeo";
import { toast } from "sonner";

const ADMIN_ROLES = ["admin", "super_admin"];

function AdminLogin() {
  const { login } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(form.email, form.password);
      if (!ADMIN_ROLES.includes(u.role)) toast.error("Dit account is geen beheerder");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F5F1] p-6" data-testid="admin-login">
      <div className="w-full max-w-sm">
        <p className="font-display text-2xl tracking-[0.42em] font-semibold text-center mb-2">ONUSA</p>
        <p className="label-x text-center mb-8">BEHEEROMGEVING</p>
        <form onSubmit={submit} className="space-y-4">
          <input required type="email" placeholder="E-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-white border border-[#E8E1D8] px-3 py-2.5 text-sm outline-none focus:border-[#1D1D1B]" data-testid="admin-email" />
          <input required type="password" placeholder="Wachtwoord" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full bg-white border border-[#E8E1D8] px-3 py-2.5 text-sm outline-none focus:border-[#1D1D1B]" data-testid="admin-password" />
          <button disabled={busy} className="btn-x w-full" data-testid="admin-login-btn">{busy ? "…" : "INLOGGEN"}</button>
        </form>
        <Link to="/" className="label-x block text-center mt-6">← TERUG NAAR WINKEL</Link>
      </div>
    </div>
  );
}

const NAV = [
  ["", "Dashboard", LayoutDashboard],
  ["products", "Producten", Package],
  ["orders", "Bestellingen & AJDN", ShoppingCart],
  ["support", "Klantenservice", MessageSquare],
  ["homepage", "Startpagina CMS", LayoutTemplate],
  ["collections", "Collecties", Layers],
  ["lookbooks", "Lookbooks", BookOpen],
  ["pages", "Content pagina's", FileText],
  ["media", "Mediabibliotheek", Image],
  ["discounts", "Kortingen", Tag],
  ["reviews", "Recensies", Star],
  ["customers", "Klanten", Users],
  ["settings", "Instellingen", Settings],
];

const MARKETING_NAV = [
  ["marketing", "Overzicht", Megaphone],
  ["marketing/contacts", "Contacten", Users],
  ["marketing/campaigns", "Campagnes", Mail],
  ["marketing/segments", "Segmenten", Filter],
  ["marketing/templates", "Sjablonen", LayoutTemplate],
  ["marketing/automations", "Automatiseringen", Workflow],
  ["marketing/analytics", "Analyse", BarChart3],
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  useSeo({ title: "ONUSA Beheer" });
  if (user === null) return <div className="min-h-screen flex items-center justify-center"><p className="label-x">LADEN…</p></div>;
  if (!user || !ADMIN_ROLES.includes(user.role)) return <AdminLogin />;

  return (
    <div className="min-h-screen flex bg-[#F7F5F1]" data-testid="admin-shell">
      <aside className="w-60 shrink-0 border-r border-[#E8E1D8] bg-white hidden md:flex flex-col sticky top-0 h-screen">
        <div className="p-6 border-b border-[#E8E1D8]">
          <p className="font-display text-lg tracking-[0.35em] font-semibold">ONUSA</p>
          <p className="label-x mt-1">BEHEER</p>
        </div>
        <nav className="flex-1 overflow-y-auto no-scrollbar py-3">
          {NAV.map(([path, label, Icon]) => (
            <NavLink key={path} to={`/admin/${path}`} end={path === ""}
              className={({ isActive }) => `flex items-center gap-3 px-6 py-2.5 text-xs uppercase tracking-[0.12em] transition-colors ${isActive ? "bg-[#1D1D1B] text-[#F7F5F1]" : "hover:bg-[#E8E1D8]/50"}`}
              data-testid={`admin-nav-${path || "dashboard"}`}>
              <Icon size={16} /> {label}
            </NavLink>
          ))}
          <p className="label-x px-6 pt-5 pb-2 text-[#77736D]">MARKETING</p>
          {MARKETING_NAV.map(([path, label, Icon]) => (
            <NavLink key={path} to={`/admin/${path}`} end={path === "marketing"}
              className={({ isActive }) => `flex items-center gap-3 px-6 py-2.5 text-xs uppercase tracking-[0.12em] transition-colors ${isActive ? "bg-[#1D1D1B] text-[#F7F5F1]" : "hover:bg-[#E8E1D8]/50"}`}
              data-testid={`admin-nav-${path.replace("/", "-")}`}>
              <Icon size={16} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#E8E1D8]">
          <p className="label-x truncate">{user.email}</p>
          <button onClick={logout} className="label-x mt-2" data-testid="admin-logout">UITLOGGEN</button>
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        <div className="md:hidden flex gap-2 overflow-x-auto no-scrollbar p-3 border-b border-[#E8E1D8] bg-white">
          {[...NAV, ...MARKETING_NAV].map(([path, label]) => (
            <NavLink key={path} to={`/admin/${path}`} end={path === "" || path === "marketing"} className={({ isActive }) => `whitespace-nowrap label-x px-3 py-1.5 border ${isActive ? "bg-[#1D1D1B] text-[#F7F5F1] border-[#1D1D1B]" : "border-[#E8E1D8]"}`}>{label}</NavLink>
          ))}
        </div>
        <div className="p-5 md:p-8"><Outlet /></div>
      </div>
    </div>
  );
}
